import { authConfig } from "./authConfig";
import { decodeJwtPayload } from "./jwt";
import { recordConsentRequired, clearConsentRequired } from "./authDiagnostics";

/* global OfficeRuntime */

/** Raised when the OBO exchange fails (e.g. consent/interaction required). */
export class SsoExchangeError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: unknown
  ) {
    super(message);
    this.name = "SsoExchangeError";
  }
}

/**
 * Gets the Office SSO bootstrap token for the signed-in Outlook identity.
 * Only works for Microsoft work/school accounts; personal accounts throw and
 * are handled by the MSAL fallback. REQ-AUTH-001.
 */
export async function getSsoBootstrapToken(): Promise<string> {
  // Deliberately no forMSGraphAccess: Office rejects it with 13012 ("API is not
  // supported in this platform") for sideloaded add-ins. All it does is pre-check
  // Graph consent, which is unnecessary here — the backend does the OBO exchange,
  // and missing consent surfaces as SsoExchangeError, which already falls back to
  // interactive sign-in.
  return OfficeRuntime.auth.getAccessToken({
    allowSignInPrompt: true,
    allowConsentPrompt: true,
  });
}

/**
 * Sends the SSO bootstrap token to the Azure Function OBO endpoint and returns
 * a Microsoft Graph access token. REQ-AUTH-004.
 */
export async function exchangeSsoForGraphToken(ssoToken: string): Promise<string> {
  const response = await fetch(`${authConfig.backendUrl}/api/auth/obo`, {
    method: "POST",
    headers: { Authorization: `Bearer ${ssoToken}` },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const detail = body as { error?: string; consentRequired?: boolean };
    // The user's own tenant, read from the SSO token they just presented. Needed
    // to build an admin-consent link for their organisation rather than ours.
    if (detail.consentRequired) {
      recordConsentRequired(tenantFromToken(ssoToken));
    }
    throw new SsoExchangeError(
      detail.error ?? `OBO exchange failed (${response.status})`,
      response.status,
      body
    );
  }

  // A successful exchange means consent is in place, so drop any earlier
  // consent prompt — otherwise it would linger for the rest of the session.
  clearConsentRequired();
  const data = (await response.json()) as { accessToken: string };
  return data.accessToken;
}

/** Tenant id from the Office SSO token, for building an admin-consent link. */
function tenantFromToken(ssoToken: string): string | undefined {
  const tid = decodeJwtPayload(ssoToken)?.tid;
  return typeof tid === "string" && tid ? tid : undefined;
}
